/**
 * Legacy undecryptable-row cleanup task (per-message refetch-storm fix).
 *
 * Background:
 * - Pre-v14 message rows that failed to decrypt were stored as
 *   `decryptionStatus:"failed"` but DISCARDED their ciphertext (the v14
 *   ciphertext-at-rest sidecar didn't exist yet). The decryption-retry
 *   orchestrator's only recovery for such a row is a PER-MESSAGE server
 *   `GET /messages/:id`. On a real account with a large pre-v14 backlog this
 *   fans out into a refetch STORM (observed ~5,400 GET/hr across devices).
 *
 * What this module does (once per client):
 * - DELETES the legacy class (`decryptionStatus:"failed" && ciphertext == null`)
 *   from `messages`, and RESETS the affected chats' progressive-load flags
 *   (`firstPhaseMsgListInit`/`fullMsgListInit`, plus the batch counters) so the
 *   normal bulk message-list loader (`umbra_progressivelyLoadMessages`) re-pages
 *   those chats 100-200 at a time WHEN OPENED — decrypting them fresh — instead
 *   of the orchestrator fetching them one-by-one. If a deleted row was a chat's
 *   denormalized `lastMessage` preview, that preview is repaired to the newest
 *   remaining local message (or cleared) so the chat list isn't left stale.
 *
 * Why deleting is safe: these rows are ALREADY undecryptable (no readable
 * content is lost). Re-fetch restores them decrypted. Rows the server has since
 * deleted simply stay gone — they were unreadable anyway.
 *
 * Atomicity (dual-review HIGH #1): the delete + flag-reset + preview-repair for
 * EACH chat run inside a single Dexie `rw` transaction, so a mid-run IDB failure
 * can never leave a chat with rows deleted but its `*Init` flags still true
 * (which would strand those messages — they'd neither be present nor re-paged).
 * A partial run just leaves un-processed chats untouched; the guard is set only
 * after every chat completes, so a re-run resumes cleanly.
 *
 * Constraints (mirrors `legacyDecryptionRepair.ts`):
 * - Runs OUTSIDE any IDB upgrade transaction — data repair, NOT a schema
 *   migration (a bad migration can block DB open / freeze startup).
 * - Chunked scan + yields between chats so a huge table doesn't block the main
 *   thread. Each per-chat transaction does ONLY Dexie ops (no awaited
 *   non-Dexie promise) so it can't auto-commit mid-flight.
 * - Idempotent: localStorage-guarded (versioned key), set only after success.
 * - Fail-soft: never crashes the app. Skips rows the orchestrator is mid-retry
 *   on (`isRowInflight`) to avoid racing a concurrent pass.
 * - Does NOT trigger any fetch itself — re-paging happens lazily on chat open.
 *
 * NOTE (dual-review #2, follow-up): the re-page loader currently RE-MINTS the
 * ciphertext-less failed class for rows that still can't decrypt, so this clears
 * the existing backlog but doesn't kill the class at the source — the
 * orchestrator throttle (companion commit) bounds those re-mints. The source fix
 * (make the loader retain ciphertext on failure) is a fast-follow.
 */

import { db } from "@/indexedDB/db";
import { ChatEntity, MessageEntity } from "@/indexedDB/types";
import { createLogger } from "@/utils/debug";
import { featureFlags } from "@/config/featureFlags";
import { isRowInflight } from "@/middleware/decryptionRetryOrchestrator";
import { safeStorage } from "@/utils/safeStorage";

const log = createLogger("LegacyUndecryptableCleanup");

// Versioned so a future revision can re-run intentionally by bumping the suffix.
const CLEANUP_FLAG_KEY = "legacyUndecryptableCleanupCompleted.v1";
const CHUNK_SIZE = 200;

/**
 * The storm class: a failed row whose ciphertext sidecar is absent, so the only
 * recovery is a per-message server GET. Rows WITH ciphertext decrypt locally
 * (no network) and must be left alone.
 */
const isLegacyUndecryptable = (msg: MessageEntity): boolean =>
  msg.decryptionStatus === "failed" && msg.ciphertext == null;

/** 0ms-timeout yield so work is split across macrotasks. */
const yieldToMainThread = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

/**
 * Scan the `messages` table in primary-key chunks and group the legacy class by
 * chatId WITHOUT mutating anything. Cursor-based (`.above(key)`) pagination
 * avoids Dexie's O(n²) `.offset()`. Rows the orchestrator is mid-retry on are
 * skipped (avoid racing a concurrent pass).
 */
const collectLegacyByChat = async (): Promise<Map<string, string[]>> => {
  const byChat = new Map<string, string[]>();
  let lastKey: string | null = null;

  for (;;) {
    const query: ReturnType<typeof db.messages.orderBy> =
      lastKey === null
        ? db.messages.orderBy("_clientId")
        : db.messages.where("_clientId").above(lastKey);
    const chunk: MessageEntity[] = await query.limit(CHUNK_SIZE).toArray();
    if (chunk.length === 0) {
      break;
    }
    lastKey = chunk[chunk.length - 1]._clientId;

    for (const msg of chunk) {
      if (isLegacyUndecryptable(msg) && !isRowInflight(msg.id)) {
        const ids = byChat.get(msg.chatId);
        if (ids) {
          ids.push(msg._clientId);
        } else {
          byChat.set(msg.chatId, [msg._clientId]);
        }
      }
    }

    if (chunk.length < CHUNK_SIZE) {
      break;
    }
    await yieldToMainThread();
  }

  return byChat;
};

/**
 * Atomically clean one chat: delete its legacy rows, reset its progressive-load
 * flags, and repair its denormalized `lastMessage` preview if it pointed at a
 * deleted legacy row. All Dexie-only ops in a single `rw` transaction so a
 * failure rolls the whole chat back (no deleted-but-not-reset strand).
 */
const cleanupChat = async (
  chatId: string,
  clientIds: string[],
): Promise<void> => {
  await db.transaction("rw", db.messages, db.chats, async () => {
    await db.messages.bulkDelete(clientIds);

    const chat = await db.chats.get(chatId);
    if (!chat) {
      return;
    }

    const updates: Partial<ChatEntity> = {
      // Force the loader to re-page this chat's history in bulk on next open.
      firstPhaseMsgListInit: false,
      fullMsgListInit: false,
      batchCount: undefined,
      renderedBatchCount: undefined,
    };

    const lm = chat.lastMessage;
    if (lm && lm.decryptionStatus === "failed" && lm.ciphertext == null) {
      // Newest REMAINING message (queried after the delete, inside the txn).
      // UUID7 ids sort chronologically; matches syncEngine's lastMessage rule.
      const newest = await db.messages
        .where("[chatId+id]")
        .between([chatId, ""], [chatId, "￿"])
        .last();
      updates.lastMessage = newest ?? null;
      updates.lastMessageTimestamp = newest?.timestamp;
      updates.lastMessageCiphertext = undefined;
    }

    await db.chats.update(chatId, updates);
  });
};

/**
 * Run the cleanup. Safe to call repeatedly — the localStorage flag
 * short-circuits subsequent runs and the filter is precise. Errors are
 * swallowed after logging. Returns counts for tests.
 */
export const runLegacyUndecryptableCleanup = async (): Promise<{
  ran: boolean;
  deletedMessages: number;
  resetChats: number;
}> => {
  if (!featureFlags.decryptionRetry) {
    return { ran: false, deletedMessages: 0, resetChats: 0 };
  }

  if (safeStorage.get(CLEANUP_FLAG_KEY) === "1") {
    return { ran: false, deletedMessages: 0, resetChats: 0 };
  }

  try {
    const byChat = await collectLegacyByChat();

    let deletedMessages = 0;
    let resetChats = 0;
    for (const [chatId, clientIds] of byChat) {
      // One atomic transaction per chat — a failure here rolls THIS chat back
      // and aborts the run WITHOUT setting the guard, so a re-run resumes the
      // remaining chats cleanly (no deleted-but-not-reset strand).
      await cleanupChat(chatId, clientIds);
      deletedMessages += clientIds.length;
      resetChats += 1;
      await yieldToMainThread();
    }

    log.info("Legacy undecryptable cleanup complete", {
      deletedMessages,
      resetChats,
    });

    // Storage write failure is non-fatal (safeStorage no-ops); a next run repeats.
    safeStorage.set(CLEANUP_FLAG_KEY, "1");

    return { ran: true, deletedMessages, resetChats };
  } catch (error) {
    // Do NOT set the guard — a re-run resumes the remaining chats. Already-
    // processed chats had their delete+reset committed atomically together.
    log.error("Legacy undecryptable cleanup failed", error);
    return { ran: false, deletedMessages: 0, resetChats: 0 };
  }
};

// Test-only helpers.
export const _CLEANUP_FLAG_KEY = CLEANUP_FLAG_KEY;
export const _isLegacyUndecryptable = isLegacyUndecryptable;
